import { bigIntToUuid } from '@bazaar-marketplace/utils';
import { env } from '~/env/client';
import bs58 from 'bs58';

export const isMobileDevice = () =>
  /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

interface PaginatedResponse<T> {
  data: {
    data: T[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const getNextPageParam = <T>(lastPage: PaginatedResponse<T>) => {
  const { page, totalPages } = lastPage.data;
  return page < totalPages ? page + 1 : undefined;
};

export function tobet(...args: unknown[]): string {
  return String(args[0]);
}

interface RefDeeplinkParams {
  type: 'ref';
  value: string;
}

interface UrlDeeplinkParams {
  type: 'url';
  value: string;
}

interface TransferDeeplinkParams {
  type: 'transfer';
  collectionId: string;
  packId: string;
  instance: string;
  marketplaceId: string;
}

interface OauthDeeplinkParams {
  type: 'oauth';
  clientId: string;
  redirectUri: string;
  state: string;
}

type DeeplinkParams = OauthDeeplinkParams | RefDeeplinkParams | TransferDeeplinkParams | UrlDeeplinkParams;

export function generateDeeplink(params: RefDeeplinkParams | UrlDeeplinkParams): string {
  const url = new URL(env.NEXT_PUBLIC_BOT_APP_URL);
  const startappValue = `${params.type}:${params.value}`;
  // we must encode the code via bs58, adding special characters (even url-encoded) doesn't open the app
  const encodedValue = bs58.encode(Buffer.from(startappValue, 'utf8'));
  url.searchParams.set('startapp', encodedValue);
  return url.toString();
}

export function parseStartParam(startParam: string | undefined): DeeplinkParams | null {
  if (!startParam) {
    return null;
  }

  // Check for transfer format first (not base58 encoded): col_1_pac_2_ins_3_mid_8071574253
  const transferMatch = /^col_(\d+)_pac_(\d+)_ins_(\d+)_mid_(.+)$/.exec(startParam);
  if (transferMatch) {
    const collectionId = transferMatch[1];
    const packId = transferMatch[2];
    const instance = transferMatch[3];
    const marketplaceId = transferMatch[4];

    if (!collectionId || !packId || !instance || !marketplaceId) {
      return null;
    }

    return {
      type: 'transfer',
      collectionId,
      packId,
      instance,
      marketplaceId,
    };
  }

  // Try to decode base58 encoded value for ref/url types
  try {
    const decoded = bs58.decode(startParam);
    const decodedString = Buffer.from(decoded).toString('utf8');

    // Check if it matches our format (type:value)
    if (decodedString.includes(':')) {
      const [type, ...rest] = decodedString.split(':');
      const value = rest.join(':');

      if (type === 'ref' || type === 'url') {
        if (!value) {
          return null;
        }
        return { type: type, value };
      }

      if (type === 'oauth') {
        const parts = value.split('|');
        const clientId = parts[0];
        const encodedRedirectUri = parts[1];
        const state = parts[2];

        if (!clientId || !encodedRedirectUri || !state) {
          return null;
        }

        // Decode base64url without relying on Buffer's base64url support (not available in all browser polyfills)
        const base64 = encodedRedirectUri.replace(/-/g, '+').replace(/_/g, '/');
        const redirectUri = decodeURIComponent(
          atob(base64)
            .split('')
            .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
            .join(''),
        );
        return { type: 'oauth', clientId, redirectUri, state };
      }
    }
  } catch {
    // If base58 decode fails, continue
  }
  return null;
}

/**
 * Maps partner marketplace IDs to our internal pack ID
 * Based on getUserOwnership mapping logic from partner.service.ts
 */
export interface PartnerPackIdentifier {
  collectionId: string; // BigInt string from partner
  packId: string; // BigInt string from partner
  instance: string; // BigInt string from partner
}

const WRAPPED_PACKS_SINGLETON_COLLECTION_ID = '676f6f64-6965-7300-0000-100000000001';

export interface PartnerPackQuery {
  brandId?: string;
  collectionId?: string;
  numberInCollection?: string;
  dropId?: string;
  numberInDrop?: string;
  isWrapped: boolean;
}

export function mapPartnerIdsToPackQuery(partnerIds: PartnerPackIdentifier): PartnerPackQuery {
  const collectionUuid = bigIntToUuid(partnerIds.collectionId);

  // Check if this is a wrapped pack (singleton collection ID)
  if (collectionUuid === WRAPPED_PACKS_SINGLETON_COLLECTION_ID) {
    // Wrapped pack: packId -> dropId, instance -> numberInDrop
    return {
      dropId: bigIntToUuid(partnerIds.packId),
      numberInDrop: partnerIds.instance,
      isWrapped: true,
    };
  }

  // Unwrapped pack: collectionId -> brandId, packId -> collectionId, instance -> numberInCollection
  return {
    brandId: collectionUuid,
    collectionId: bigIntToUuid(partnerIds.packId),
    numberInCollection: partnerIds.instance,
    isWrapped: false,
  };
}
